import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';

type Stage = 'ingest' | 'vision' | 'transcript';

const STAGES: Array<{ id: Stage; label: string; detail: string }> = [
  {
    id: 'vision',
    label: 'AI analysis',
    detail: 'Object and scene tags, face detection, and search embeddings.',
  },
  {
    id: 'ingest',
    label: 'Metadata and thumbnails',
    detail: 'Re-reads EXIF, regenerates thumbnails and preview videos.',
  },
  {
    id: 'transcript',
    label: 'Speech to text',
    detail: 'Transcribes anything with audio. Slowest of the three.',
  },
];

interface Props {
  /** What to reprocess: an explicit selection, or everything in a folder. */
  target: { assetIds: string[] } | { folderId: string; folderName: string };
  /**
   * Whether "redo work already done" starts ticked.
   *
   * Reanalysing one specific file you are looking at almost always means "run
   * it again", so callers acting on a single asset pass true. A bulk or
   * folder-wide action defaults to false, where the usual intent is filling in
   * what is missing rather than redoing hours of GPU work.
   */
  defaultForce?: boolean;
  onClose: () => void;
  /** Called after work is queued, so a detail view can refresh its status. */
  onQueued?: () => void;
}

/**
 * Re-runs the processing pipeline over existing media.
 *
 * The case this exists for: the pipeline changed, not the files. After
 * enabling face detection, upgrading a model, or fixing a bug that produced
 * bad metadata, the alternative would be re-uploading the archive.
 */
export function ReindexDialog({
  target,
  defaultForce = false,
  onClose,
  onQueued,
}: Props): ReactNode {
  const [stages, setStages] = useState<Set<Stage>>(new Set(['vision']));
  const [force, setForce] = useState(defaultForce);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isFolder = 'folderId' in target;
  const label = isFolder
    ? `everything in "${target.folderName}"`
    : `${target.assetIds.length} selected file(s)`;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const response = await api.reindex({
        ...(isFolder ? { folderId: target.folderId, recursive: true } : { assetIds: target.assetIds }),
        stages: [...stages],
        force,
      });

      if (response.queued === 0) {
        setResult(
          force
            ? 'Nothing matched.'
            : 'Everything selected has already been processed. Tick "redo work that is ' +
              'already done" to run it again anyway.',
        );
      } else {
        onQueued?.();
        const parts = Object.entries(response.byStage)
          .filter(([, n]) => n > 0)
          .map(([stage, n]) => `${n} ${stage}`);
        setResult(
          `Queued ${parts.join(', ')}. Progress is in Admin → Processing.` +
            (response.truncated
              ? ' Capped at 5000 files — run it again to continue.'
              : ''),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start reindexing');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (stage: Stage): void => {
    const next = new Set(stages);
    if (next.has(stage)) next.delete(stage);
    else next.add(stage);
    setStages(next);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{ maxWidth: 520, width: '90%', margin: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>Reanalyze {label}</h3>

        {result ? (
          <>
            <div className="notice success">{result}</div>
            <button className="btn primary" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
              Re-runs processing over media that is already stored. Useful after turning on a
              feature or upgrading a model — the files are unchanged, the pipeline is not.
            </p>

            {error && <div className="notice error">{error}</div>}

            <div className="stack" style={{ marginBottom: 14 }}>
              {STAGES.map((stage) => (
                <label key={stage.id} className="flex" style={{ alignItems: 'flex-start', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={stages.has(stage.id)}
                    onChange={() => toggle(stage.id)}
                    style={{ width: 'auto', marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ fontSize: 13 }}>{stage.label}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{stage.detail}</div>
                  </span>
                </label>
              ))}
            </div>

            <label className="flex" style={{ alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                style={{ width: 'auto', marginTop: 3 }}
              />
              <span>
                <strong style={{ fontSize: 13 }}>Redo work that is already done</strong>
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  Off: only processes files with no result yet — the right choice after
                  enabling a feature. On: reprocesses everything, for a model upgrade where
                  existing results are stale rather than missing.
                </div>
              </span>
            </label>

            {isFolder && force && (
              <div className="notice">
                This reprocesses every file in the folder and its subfolders. On a large
                folder that is hours of GPU time.
              </div>
            )}

            <div className="flex">
              <button
                className="btn primary"
                disabled={busy || stages.size === 0}
                onClick={() => void submit()}
              >
                {busy ? 'Queuing…' : 'Start'}
              </button>
              <button className="btn" onClick={onClose} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
